/** 組織図。箱の中身はサーバー側(/api/hr/org/chart)が組み立てたものをそのまま描く。
 *
 * 配置は根を左に置いて配下を右へ広げる(マインドマップ形。HIDE 2026-10-19)。
 * 画面がやるのは配置(d3-org-chart)と操作(拡大・移動・開閉・検索)だけ。印刷用は
 * サーバーが描く SVG(/api/hr/org/chart.svg)を別タブで開く。
 */
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  Segmented,
  Space,
  Spin,
  Tag,
  TreeSelect,
  Typography,
  message,
} from "antd";
import { OrgChart } from "d3-org-chart";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { ChartNode } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, unitOptions, useOpenChartFile, useOrgUnits } from "./hooks";

const BOX_W = 260;
const HEAD_H = 52;
const ROW_H = 19;
const GROUP_H = 20;

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** 箱の高さ。中身(責任者 + 見出し + 氏名)から決める。 */
function boxHeight(d: ChartNode): number {
  const rows = d.groups.reduce((n, g) => n + 1 + g.members.length, 0);
  return HEAD_H + (d.head ? ROW_H : 0) + rows * ROW_H + 18;
}

function memberRow(m: ChartNode["groups"][number]["members"][number]): string {
  const color = m.partner ? "#15803d" : "#111827";
  const note = m.note ? ' <span style="color:#b91c1c">※</span>' : "";
  return (
    `<div style="color:${color};font-size:11px;line-height:${ROW_H}px;` +
    `overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(m.name)}${note}</div>`
  );
}

/** 1箱のHTML。SVG 版(server/app/hr/org_chart.py)と同じ順・同じ色で出す。 */
function nodeHtml(d: ChartNode): string {
  const groups = d.groups
    .map(
      (g) =>
        `<div style="color:#6b7280;font-size:10px;line-height:${GROUP_H}px">${esc(g.label)}</div>` +
        g.members.map(memberRow).join(""),
    )
    .join("");
  const head = d.head
    ? `<div style="font-size:11px;line-height:${ROW_H}px;font-weight:600;color:#111827">` +
      `${esc(d.head.title)}: ${esc(d.head.name)}</div>`
    : "";
  return (
    `<div class="org-node" data-unit-id="${d.id}" style="width:${BOX_W}px;height:100%;` +
    `box-sizing:border-box;border:1px solid #d1d5db;border-radius:8px;background:#fff;` +
    `overflow:hidden;font-family:system-ui,sans-serif">` +
    `<div style="background:${d.fill};color:#fff;padding:6px 8px">` +
    `<div style="font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;` +
    `white-space:nowrap">${esc(d.name)}</div>` +
    `<div style="font-size:10px;opacity:.85">${esc(d.kind_label)} ・ 所属 ${d.member_count}名 ` +
    `/ 配下計 ${d.total_count}名</div></div>` +
    `<div style="padding:6px 8px">${head}${groups}</div></div>`
  );
}

/** グループ会社があると根が複数になる。d3-org-chart は根1つなので束ねる箱を足す。 */
const GROUP_ROOT = -1;

function chartData(items: ChartNode[]): ChartNode[] {
  const roots = items.filter((d) => d.parent_id == null);
  if (roots.length <= 1) return items;
  const total = roots.reduce((n, r) => n + r.total_count, 0);
  return [
    {
      id: GROUP_ROOT,
      parent_id: null,
      name: "グループ全体",
      kind: "company",
      kind_label: `会社 ${roots.length} 社`,
      path: "",
      fill: "#134e4a",
      head: null,
      member_count: 0,
      total_count: total,
      groups: [],
    },
    ...items.map((d) => (d.parent_id == null ? { ...d, parent_id: GROUP_ROOT } : d)),
  ];
}

/** 検索語に当たる組織(組織名・経路・責任者・メンバーの氏名を見る)。 */
function matches(items: ChartNode[], q: string): number[] {
  const needle = q.trim();
  if (!needle) return [];
  return items
    .filter((d) => {
      if (d.name.includes(needle) || d.path.includes(needle)) return true;
      if (d.head && d.head.name.includes(needle)) return true;
      return d.groups.some((g) => g.members.some((m) => m.name.includes(needle)));
    })
    .map((d) => d.id);
}

/** 図の高さ。fit() はこの箱の中で合わせるので、描画領域と一致させる。 */
const CHART_H = 620;

export function OrgChartPage() {
  const api = useApi();
  const navigate = useNavigate();
  const units = useOrgUnits(true);
  const openFile = useOpenChartFile();
  const [scope, setScope] = useState<"direct" | "all">("direct");
  const [root, setRoot] = useState<number | undefined>(undefined);
  const [keyword, setKeyword] = useState("");
  const [found, setFound] = useState<string>("");
  const holder = useRef<HTMLDivElement>(null);
  const chart = useRef<OrgChart<ChartNode> | null>(null);

  const query = useQuery({
    queryKey: ["hr", "chart", scope, root ?? 0],
    queryFn: () =>
      api.orgChart(
        new URLSearchParams({ scope, ...(root ? { root: String(root) } : {}) }),
      ),
  });
  const items = query.data?.items;

  useEffect(() => {
    if (!holder.current || !items || items.length === 0) return;
    const c = chart.current ?? new OrgChart<ChartNode>();
    chart.current = c;
    c.container(holder.current)
      .svgHeight(CHART_H)
      .data(chartData(items))
      .nodeId((d) => d.id)
      .parentNodeId((d) => d.parent_id ?? "")
      .nodeWidth(() => BOX_W)
      .nodeHeight((d) => boxHeight(d.data))
      // 根を左に置き、配下を右へ広げる(マインドマップ形)。箱は縦に積むので
      // 兄弟の間は詰め、親子の間(横)は矢印が読める幅を取る
      .childrenMargin(() => 70)
      .siblingsMargin(() => 16)
      .compact(false)
      .layout("left")
      .initialExpandLevel(99)
      .scaleExtent([0.2, 3])
      .nodeContent((d) => nodeHtml(d.data))
      .onNodeClick((d) => {
        // 箱をクリックしたら、その組織で絞った従業員一覧へ移る(束ねた箱は除く)
        if (d.data.id > 0) navigate(`/hr/employees?org_unit_id=${d.data.id}`);
      })
      .render()
      .fit();
    // 描き直した直後はまだノードが動いている(d3-org-chart の既定は 800ms)。
    // その間に測った fit は外れるので、落ち着いてからもう一度合わせる
    const t = window.setTimeout(() => chart.current?.fit(), 900);
    return () => window.clearTimeout(t);
  }, [items, navigate]);

  const search = useCallback(() => {
    const c = chart.current;
    if (!c || !items) return;
    const ids = matches(items, keyword);
    c.clearHighlighting();
    if (!keyword.trim()) {
      setFound("");
      return;
    }
    if (ids.length === 0) {
      setFound("該当なし");
      void message.info("該当する組織・氏名が無い");
      return;
    }
    c.expandAll();
    ids.forEach((id) => c.setUpToTheRootHighlighted(id));
    c.setCentered(ids[0]);
    setFound(`${ids.length} 件の組織に該当`);
  }, [items, keyword]);

  const svgPath = `/api/hr/org/chart.svg?scope=${scope}${root ? `&root=${root}` : ""}`;
  const mmdPath = `/api/hr/org/chart.mmd?scope=${scope}${root ? `&root=${root}` : ""}`;

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          組織図
        </Typography.Title>
        <Space>
          <Button onClick={() => void openFile(svgPath)}>印刷用SVG(別タブ)</Button>
          <Button onClick={() => void openFile(mmdPath)}>Mermaid定義</Button>
        </Space>
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex gap={8} wrap align="center">
          <Segmented<"direct" | "all">
            value={scope}
            onChange={(v) => setScope(v)}
            options={[
              { value: "direct", label: "直雇用だけ" },
              { value: "all", label: "全員(協力会社を含む)" },
            ]}
          />
          <TreeSelect
            allowClear
            treeDefaultExpandAll
            style={{ width: 260 }}
            placeholder="根にする組織(既定は会社)"
            value={root}
            treeData={unitOptions(units.data?.items ?? [])}
            onChange={(v: number | undefined) => setRoot(v)}
          />
          <Input
            placeholder="氏名・組織名で探す"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={search}
            style={{ width: 220 }}
            allowClear
          />
          <Button onClick={search}>探す</Button>
          {found && <Tag color="blue">{found}</Tag>}
          <Space>
            <Button onClick={() => chart.current?.expandAll().fit()}>全部開く</Button>
            <Button onClick={() => chart.current?.collapseAll().fit()}>全部閉じる</Button>
            <Button onClick={() => chart.current?.fit()}>ぴったり表示</Button>
            <Button onClick={() => chart.current?.zoomIn()}>＋</Button>
            <Button onClick={() => chart.current?.zoomOut()}>−</Button>
          </Space>
        </Flex>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          ドラッグで移動・ホイールで拡大縮小。箱をクリックすると、その組織の従業員一覧へ移る。
          緑の氏名は直雇用ではない人(協力会社・派遣・業務委託)。
        </Typography.Text>
      </Card>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="組織図を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      {query.isLoading && (
        <Flex justify="center" style={{ padding: 64 }}>
          <Spin />
        </Flex>
      )}

      {items && items.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="組織がまだ無い"
          description="組織マスタで会社と配下の組織を作ると、ここに図が出る。"
        />
      )}

      <div
        data-testid="org-chart"
        ref={holder}
        style={{ background: "#fff", borderRadius: 8, minHeight: CHART_H }}
      />
    </div>
  );
}
